import { isEqualWith } from 'lodash-es';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { AccaBoostToken } from '../../reward-tokens/reward-tokens.model';
import { getEligibleAccaBoost, getSelectedToken } from '../../reward-tokens/services/linear-reward-tokens.utils';
import { findMinimumLegsAccaTokens } from '../../reward-tokens/services/reward-tokens.utils';
import { IRewardTokenEligibilityState, TokensRecord } from '../../reward-tokens/state';
import { IBetslipTypeState } from '../../types/state';
import { AccaBoostContext } from '../model';

export function compareContexts(context1: AccaBoostContext, context2: AccaBoostContext): boolean {
    return isEqualWith(context1, context2, (value1, value2, key) => {
        switch (key as keyof AccaBoostContext) {
            case 'token':
                return value1?.id === value2?.id;

            case 'isSelected':
            case 'isOneLegAway':
            case 'maxReward':
            case 'picksCount':
            case 'validCombo':
                return value1 === value2;

            default:
                return;
        }
    });
}

export function getAccaTokenStatus(
    eligibilityState: IRewardTokenEligibilityState,
    tokens: TokensRecord,
    types: IBetslipTypeState,
    picks: BetslipPick[],
    hasComboPrevention: boolean,
    isLinear: boolean,
): { token: AccaBoostToken | undefined; isOneLegAway: boolean; isSelected: boolean } {
    const status =
        resolveEligibleStatus(eligibilityState, tokens, types) ||
        resolveOneLegAwayStatus(eligibilityState, tokens, types, picks, hasComboPrevention, isLinear);

    return {
        token: undefined,
        isOneLegAway: false,
        isSelected: false,
        ...status,
    };
}

function resolveEligibleStatus(
    eligibilityState: IRewardTokenEligibilityState,
    tokens: TokensRecord,
    types: IBetslipTypeState,
): { token: AccaBoostToken; isSelected: boolean } | undefined {
    const token = getEligibleAccaBoost(eligibilityState, tokens);
    const selectedToken = getSelectedToken(tokens, types.comboBet);

    if (!token) {
        return;
    }

    return {
        token,
        isSelected: token.userTokenId === selectedToken?.userTokenId,
    };
}

function resolveOneLegAwayStatus(
    eligibilityState: IRewardTokenEligibilityState,
    tokens: TokensRecord,
    types: IBetslipTypeState,
    picks: BetslipPick[],
    hasComboPrevention: boolean,
    isLinear: boolean,
): { token: AccaBoostToken; isOneLegAway: boolean } | undefined {
    // For Multi Singles we do not resolve one leg away status in tabbed betslip
    const isMultiSingles = !isLinear && types.base.currentSelectedType === BetslipType.Single && picks.length > 1;

    if (isMultiSingles || hasComboPrevention) {
        return undefined;
    }

    // find first token that is one leg away from being eligible
    const token = findMinimumLegsAccaTokens(eligibilityState, tokens).find((item) => item.minimumLegs! - 1 === picks.length);

    if (!token) {
        return undefined;
    }

    return {
        token,
        isOneLegAway: true,
    };
}
